import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils.ts";

/**
 * A player's jersey on Play Entry (client 2026-11-03): the number in blue on a
 * pale-blue circle — a square for OL's center — that opens the jersey picker
 * (the trigger of each app's JerseyDropdown). Its size comes from the caller;
 * a longer value (a jersey with a name) steps its type down to stay inside.
 */
export const JerseyBadge = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { value: string; shape?: "circle" | "square" }
>(function JerseyBadge({ value, shape = "circle", className, ...props }, ref) {
  const length = [...value].length;
  // Sized by the longest word too, so a name breaks between words, not inside
  // one: 80px of line in the small (96px) circle, 104px in the large one.
  const longest = Math.max(0, ...value.trim().split(/\s+/).map((word) => [...word].length));
  const size =
    length <= 3
      ? "text-4xl leading-none"
      : length <= 4
        ? "text-3xl leading-none"
        : longest <= 6 && length <= 14
          ? "text-xl leading-tight"
          : longest <= 8
            ? "text-base leading-tight"
            : longest <= 10
              ? "text-[13px] leading-tight"
              : "text-xs leading-tight";
  const lines = length <= 3 || (longest <= 6 && length <= 14) ? "line-clamp-2" : "line-clamp-3";
  return (
    <button
      ref={ref}
      type="button"
      aria-label={value ? `Jersey ${value}` : "Jersey #"}
      title={value || undefined}
      {...props}
      className={cn(
        "flex aspect-square flex-shrink-0 items-center justify-center bg-[#EFF6FF] px-2 text-center font-bold text-[#1D4ED8] transition-opacity active:opacity-80",
        shape === "circle" ? "rounded-full" : "rounded-none",
        size,
        className
      )}
    >
      <span className={cn("break-words hyphens-auto", lines)}>{value}</span>
    </button>
  );
});
